import { CoreButton, CoreCheckbox, CoreInput, CoreSelect, CoreSteps } from '@components'
import CoreLoader from '@components/CoreLoader'
import BankMetadataFieldInput from '@recipients/components/BankMetadataFieldInput'
import { formatCurrencyForDisplay } from '@utils'
import {
  CurrencyTypes,
  useCountries,
  useCurrencies,
  useEditBankAccountQry,
  useMerchantQry
} from 'mmfintech-backend-api'
import type { BankFieldMetadata, Currency, RecipientResponse } from 'mmfintech-backend-api/types'
import { isValidArray, tr } from 'mmfintech-commons'
import { ErrorDisplay } from 'mmfintech-portal-commons'
import { useCallback, useMemo } from 'react'

import '../styles/BankRecipientEdit.scss'

interface Props {
  recipient?: RecipientResponse
  onSuccess?: () => void
  independentEdit?: boolean
}

const validMetadataCategory = (field: BankFieldMetadata, category: string): boolean =>
  field.code !== 'COUNTRY_CODE' && field.code !== 'HOLDER_NAME' && field.fieldCategory === category

function BankRecipientEdit({ recipient, onSuccess }: Props) {
  const recipientId = recipient?.paymentInstrumentId || 0

  const { countryOptions } = useCountries()

  const { isMerchantCompany } = useMerchantQry()

  const { currencyOptions } = useCurrencies()

  const {
    step,
    setStep,
    formValues,
    countryCode,
    bankMetadata,
    accountTypeOptions,
    documentTypesOptions,
    handleSubmit,
    providedBanks,
    isFieldVisible,
    saveRecipientError,
    saveRecipientLoading,
    bankMetadataFetching,
    bankMetadataError,
    documentTypesFetching
  } = useEditBankAccountQry({
    recipientId,
    currencyCode: recipient?.currency,
    onSuccess,
    enableProvidedBanks: true,
    independentEdit: true
  })

  const metadataCustomError = useMemo(() => {
    if (bankMetadataError && formValues.getValue('currency') && countryCode) {
      return {
        error: tr('FRONTEND.RECIPIENTS.METADATA.CUSTOM_ERROR_WITHDRAW', 'Cannot withdraw {{CURRENCY}} in {{COUNTRY}}', {
          CURRENCY: formatCurrencyForDisplay(formValues.getValue('currency')),
          COUNTRY: countryOptions?.find(x => x.value === countryCode)?.label
        })
      }
    }
    return null
  }, [bankMetadataError, formValues.getValue('currency'), countryCode])

  const handleClick = () => {
    formValues.setValue('personal', !formValues.getValue('personal'))
  }

  const filterCurrencies = useCallback(
    (c: Currency): boolean => c.supportedForWithdrawal && c.type === CurrencyTypes.FIAT,
    []
  )

  const renderFields = (category: string) =>
    isValidArray(bankMetadata) &&
    bankMetadata
      .filter(meta => validMetadataCategory(meta, category) && isFieldVisible(meta))
      .map(field => (
        <BankMetadataFieldInput
          key={field.code}
          field={field}
          formValues={formValues}
          countryCode={countryCode}
          accountTypes={accountTypeOptions}
          providedBanks={providedBanks}
          documentTypesOptions={documentTypesOptions}
        />
      ))

  return bankMetadataFetching || documentTypesFetching ? (
    <CoreLoader />
  ) : (
    <div className='recipient-edit-wrapper'>
      <form className='styled-form' noValidate>
        <div className='recipient-edit-steps'>
          <CoreSteps
            maxStep={2}
            step={step}
            title={
              step === 1
                ? tr('FRONTEND.CUSTOMERS.EDIT.TITLE_PAYMENT', 'Payment information')
                : tr('FRONTEND.CUSTOMERS.EDIT.TITLE_PERSONAL', 'Personal information')
            }
          />
        </div>
        <div className='recipient-form-title'>
          {step === 1
            ? tr('FRONTEND.CUSTOMERS.EDIT.TITLE_PAYMENT', 'Payment information')
            : tr('FRONTEND.CUSTOMERS.EDIT.TITLE_PERSONAL', 'Personal information')}
        </div>
        <div className='form-elements-container'>
          {step === 1 && (
            <>
              <CoreSelect
                type='currency'
                data-test='currency'
                label={tr('FRONTEND.ACCOUNTS.CREATE.CURRENCY_LABEL', 'Currency')}
                options={currencyOptions(filterCurrencies)}
                value={formValues.getValue('currency')}
                onChange={(_: string, val: any) => {
                  formValues.setValue('currency', val.value)
                }}
                required={formValues.isRequired('currency')}
                error={formValues.getError('currency')}
              />

              <CoreInput
                type='text'
                data-test='account-name'
                maxLength={100}
                label={tr('FRONTEND.RECIPIENTS.LABELS.ACCOUNT_NAME', 'Recipient name')}
                {...formValues.registerInput('name')}
                autoComplete='off'
              />

              {renderFields('BANK_DETAILS')}

              <CoreInput
                type='text'
                maxLength={100}
                data-test='description'
                label={tr('FRONTEND.RECIPIENTS.LABELS.DESCRIPTION', 'Description')}
                {...formValues.registerInput('description')}
                autoComplete='off'
              />
            </>
          )}

          {step === 2 && renderFields('RECIPIENT_DETAILS')}

          {step === 1 && isMerchantCompany && (
            <CoreCheckbox
              className='form-checkbox'
              type='check'
              checked={!!formValues.getValue('personal')}
              value={'personal'}
              onClick={handleClick}
              label={tr('FRONTEND.RECIPIENTS.LABELS.BANK_PERSONAL_ACCOUNT', 'This is my personal account')}
              tooltip={{
                enabled: true,
                text: tr(
                  'FRONTEND.RECIPIENTS.LABELS.CRYPTO_PERSONAL_ACCOUNT_TOOLTIP',
                  'Personal accounts are your own external accounts and are only visible to you, administrators, and authorized departments within a company account.'
                ),
                place: 'bottom',
                padding: '3px',
                backgroundColor: 'black',
                textColor: 'white'
              }}
            />
          )}
        </div>

        <ErrorDisplay error={[saveRecipientError, metadataCustomError]} />

        <div className='recipient-edit-actions'>
          {step === 2 && (
            <CoreButton
              data-test='save-button'
              type='button'
              variation='tertiary'
              size='large'
              text={tr('FRONTEND.BUTTONS.BACK', 'Back')}
              className='save-button'
              onClick={e => {
                e.preventDefault()
                setStep(1)
              }}
              disabled={saveRecipientLoading}
            />
          )}
          <CoreButton
            data-test='save-button'
            type='button'
            variation='primary-black'
            size='large'
            fullWidth
            text={tr('FRONTEND.COOKIE_CONSENT.SAVE', 'Save')}
            className='save-button'
            onClick={async e => {
              e.preventDefault()
              await handleSubmit()
            }}
            isLoading={saveRecipientLoading}
            disabled={!!metadataCustomError}
          />
          {step === 1 && (
            <CoreButton
              data-test='save-button'
              type='button'
              variation='primary-black'
              size='large'
              text={tr('FRONTEND.QUESTIONNAIRE.BUTTON_NEXT', 'Next')}
              className='save-button'
              onClick={e => {
                e.preventDefault()
                if (formValues.areValid(1)) {
                  setStep(2)
                }
              }}
              disabled={saveRecipientLoading || !!metadataCustomError}
            />
          )}
        </div>
      </form>
    </div>
  )
}

export default BankRecipientEdit
